import { PolicySummary } from './homeApi';
import { formatDisplayDateTime } from '../../common/dateTime/formatDisplayDateTime';

export interface PolicyTableRow extends Record<string, unknown> {
  uniqueId: string;
  employerGroupName: string;
  policyList: string;
  fundingType: string;
  submitterName: string;
  dateStarted: string;
  dateSubmitted: string;
  rfiStatus: string;
  downloadRfi: string;
  rawCustomerNumber: string;
  rawPolicyNumber: string;
  rawSurveyId: string;
  rawSurveyYear: string;
  rawEmployerGroupEIN: string | null;
}

function buildPolicyGroupKey(item: PolicySummary) {
  return `${item.customerNumber ?? ''}||${item.fundingType ?? ''}||${item.surveyYear ?? ''}`;
}

function normalizePolicyIdentifier(item: PolicySummary) {
  const medIdCardNum = (item.medIdCardNum ?? '').trim();
  if (medIdCardNum.length > 0) {
    return medIdCardNum;
  }

  const policyNumber = (item.policyNumber ?? '').trim();
  return policyNumber.length > 0 ? policyNumber : null;
}

export function getPolicyTableRows(
  results: PolicySummary[],
  fallbackValue: string,
  defaultStatus: string,
): PolicyTableRow[] {
  const grouped = new Map<string, { representative: PolicySummary; policyIdentifiers: Set<string> }>();

  results.forEach((item) => {
    const key = buildPolicyGroupKey(item);
    const existing = grouped.get(key);
    const policyIdentifier = normalizePolicyIdentifier(item);

    if (!existing) {
      const policyIdentifiers = new Set<string>();
      if (policyIdentifier) {
        policyIdentifiers.add(policyIdentifier);
      }

      grouped.set(key, {
        representative: item,
        policyIdentifiers,
      });
      return;
    }

    if (policyIdentifier) {
      existing.policyIdentifiers.add(policyIdentifier);
    }
  });

  const rows = Array.from(grouped.entries()).map(([key, value], index) => {
    const aggregatedPolicies = Array.from(value.policyIdentifiers);
    const firstPolicy = aggregatedPolicies[0] ?? value.representative.policyNumber ?? '';

    return {
      uniqueId: `${key}-${index}`,
      employerGroupName: value.representative.employerGroupName ?? fallbackValue,
      policyList: aggregatedPolicies.length > 0 ? aggregatedPolicies.join(', ') : fallbackValue,
      fundingType: value.representative.fundingType ?? fallbackValue,
      submitterName: value.representative.userName ?? '',
      dateStarted: formatDisplayDateTime(value.representative.surveyStartDate, ''),
      dateSubmitted: formatDisplayDateTime(value.representative.surveyCompletedDate, ''),
      rfiStatus: value.representative.surveyStatus ?? defaultStatus,
      downloadRfi: value.representative.surveyStatus === 'Complete' ? 'Download' : fallbackValue,
      rawCustomerNumber: value.representative.customerNumber ?? '',
      rawPolicyNumber: firstPolicy,
      rawSurveyId: value.representative.surveyId != null ? String(value.representative.surveyId) : '',
      rawSurveyYear: value.representative.surveyYear ?? '',
      rawEmployerGroupEIN: value.representative.employerGroupEIN ?? null,
    };
  });

  return rows.sort((left, right) => {
    return left.employerGroupName.localeCompare(right.employerGroupName, undefined, {
      sensitivity: 'base',
    });
  });
}

/**
 * Splits a comma-separated policyList string into individual values,
 * excluding the fallback placeholder.
 */
export function splitPolicyList(policyList: string, fallbackValue: string): string[] {
  if (!policyList || policyList === fallbackValue) {
    return [];
  }

  return policyList
    .split(/[,;]/)
    .map((v) => {return v.trim()})
    .filter((v) => {return v.length > 0 && v !== fallbackValue});
}

/**
 * Filters grouped PolicyTableRows by a case-insensitive text term.
 * Mirrors Angular behavior: only applies when the term reaches the configured minimum length.
 * Searches employerGroupName, policyList, and fundingType.
 */
export function filterTableRows(
  rows: PolicyTableRow[],
  filter: string,
  minApplyLength = 3,
): PolicyTableRow[] {
  const normalized = filter.trim().toLowerCase();

  if (normalized.length < minApplyLength) {
    return rows;
  }

  return rows.filter(
    (row) =>
      {return (row.employerGroupName ?? '').toLowerCase().includes(normalized)
      || (row.policyList ?? '').toLowerCase().includes(normalized)
      || (row.fundingType ?? '').toLowerCase().includes(normalized)},
  );
}
